import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type {
  FuelFacilityDocType,
  FuelFacilityLicence,
  FuelFacilityLicenceState,
  FuelFacilityType,
  Page,
  UtilityOperator,
} from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, FileHashField, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, type FileHashValue } from '../../ui/components';

const OFFICER_ROLES = ['ENERGY_OFFICER'];
const FACILITY_TYPES: FuelFacilityType[] = ['FUEL_STATION', 'STORAGE_DEPOT', 'BULK_TERMINAL'];
const FUEL_TYPES = ['PETROL', 'DIESEL', 'KEROSENE', 'LPG', 'HEAVY_FUEL_OIL'] as const;
const STATES: FuelFacilityLicenceState[] = ['APPLIED', 'REFERRALS_PENDING', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'ISSUED', 'SUSPENDED', 'REVOKED', 'EXPIRED'];
const DOC_TYPES: FuelFacilityDocType[] = ['SITE_PLAN', 'FIRE_SAFETY_CERT', 'ENVIRONMENTAL_CLEARANCE', 'INSURANCE', 'OTHER'];

/** Fuel facility (station/storage/terminal) licensing with the Fire(M06)/Health(M25)/Planning(M17) referral chain (M35). */
export function FuelFacilityLicences() {
  const { persona } = useAuth();
  const isOfficer = persona?.roles.includes('ENERGY_OFFICER') ?? false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m35/fuel-facility-licences?${params.toString()}`;
  }, [status, appliedQ]);
  const listQ = useApiQuery<Page<FuelFacilityLicence>>(path);

  return (
    <div>
      <PageHeader
        title="Fuel Facility Licences"
        subtitle="Public register — apply -> Fire(M06)/Health(M25)/Planning(M17) referral chain -> review -> approve -> SeyPay fee -> issue (M35)."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New licence application
          </button>
        }
      />

      <form
        className="mb-4 flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setAppliedQ(q);
        }}
      >
        <input className="field-input !w-56" placeholder="Ref, facility, district…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All states</option>
          {STATES.map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
        <button type="submit" className="btn-primary">
          Search
        </button>
      </form>

      {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No fuel facility licences match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Facility</th>
                <th className="table-head">Type</th>
                <th className="table-head">District</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((l) => (
                <tr key={l.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(l.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{l.refNo}</td>
                  <td className="table-cell">{l.facilityName}</td>
                  <td className="table-cell">
                    <StatusBadge status={l.facilityType} />
                  </td>
                  <td className="table-cell text-xs text-slate-500">{l.district}</td>
                  <td className="table-cell">
                    <StatusBadge status={l.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <DetailModal id={selectedId} isOfficer={isOfficer} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} />
      ) : null}
      {showCreate ? (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function DetailModal({ id, isOfficer, onClose, onChanged }: { id: string; isOfficer: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<FuelFacilityLicence>(`/api/v1/m35/fuel-facility-licences/${id}`);
  const l = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [suspendReason, setSuspendReason] = useState('');
  const [revokeReason, setRevokeReason] = useState('');
  const [doc, setDoc] = useState<FileHashValue | null>(null);
  const [docType, setDocType] = useState<FuelFacilityDocType>('SITE_PLAN');
  const [referralNotes, setReferralNotes] = useState<Record<string, string>>({});

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const allReferralsResolved = l?.referrals ? l.referrals.every((r) => r.status !== 'PENDING') : false;

  return (
    <Modal title={l ? l.refNo : 'Fuel facility licence'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {l ? (
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
            <Detail label="State" value={<StatusBadge status={l.state} />} />
            <Detail label="Type" value={<StatusBadge status={l.facilityType} />} />
            <Detail label="District" value={l.district} />
            <Detail label="Capacity" value={`${Number(l.storageCapacityLitres).toLocaleString()} L`} />
            <Detail label="Fuel types" value={l.fuelTypes.join(', ')} />
            <Detail label="Fee" value={`${scr(l.feeAmountScr)} (${l.feeStatus})`} />
            {l.licenceSerial ? <Detail label="Licence serial" value={l.licenceSerial} mono /> : null}
            {l.validTo ? <Detail label="Valid to" value={formatDate(l.validTo)} /> : null}
          </div>
          {l.rejectionReason ? <ErrorNote message={`Rejected: ${l.rejectionReason}`} /> : null}
          {l.suspendReason ? <ErrorNote message={`Suspended: ${l.suspendReason}`} /> : null}
          {l.revokedReason ? <ErrorNote message={`Revoked: ${l.revokedReason}`} /> : null}

          <div>
            <div className="mb-2 text-sm font-semibold text-slate-700">Documents ({l.documents?.length ?? 0})</div>
            {isOfficer ? (
              <div className="mb-2 flex items-end gap-2 rounded-lg border border-slate-200 p-2.5">
                <select className="field-input !w-auto" value={docType} onChange={(e) => setDocType(e.target.value as FuelFacilityDocType)}>
                  {DOC_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t.replaceAll('_', ' ')}
                    </option>
                  ))}
                </select>
                <FileHashField label="File" onChange={setDoc} />
                <button
                  className="btn-secondary"
                  disabled={busy || !doc}
                  onClick={() =>
                    act(async () => {
                      await api.post(`/api/v1/m35/fuel-facility-licences/${id}/documents`, { docType, docRef: doc!.docRef, sha256: doc!.sha256, fileName: doc!.fileName });
                      setDoc(null);
                    })
                  }
                >
                  Attach
                </button>
              </div>
            ) : null}
            {(l.documents?.length ?? 0) === 0 ? (
              <p className="text-xs text-slate-400">No supporting documents attached.</p>
            ) : (
              <ul className="space-y-1 text-xs">
                {l.documents!.map((d) => (
                  <li key={d.id} className="flex items-center justify-between rounded border border-slate-100 px-2 py-1">
                    <span>
                      <StatusBadge status={d.docType} /> {d.fileName ?? d.docRef}
                    </span>
                    <span className="text-slate-400">{formatDateTime(d.createdAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <div className="mb-2 text-sm font-semibold text-slate-700">Referral chain ({l.referrals?.length ?? 0})</div>
            {(l.referrals?.length ?? 0) === 0 ? (
              <p className="text-xs text-slate-400">
                {isOfficer && l.state === 'APPLIED' ? (
                  <button className="btn-secondary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m35/fuel-facility-licences/${id}/start-referrals`))}>
                    Start Fire/Health/Planning referrals
                  </button>
                ) : (
                  'No referrals fanned out yet.'
                )}
              </p>
            ) : (
              <div className="space-y-2">
                {l.referrals!.map((r) => (
                  <div key={r.id} className="rounded-lg border border-slate-100 px-3 py-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{r.targetModule.replaceAll('_', ' ')}</span>
                      <StatusBadge status={r.status} />
                    </div>
                    {r.notes ? <p className="mt-0.5 text-slate-500">{r.notes}</p> : null}
                    {isOfficer && r.status === 'PENDING' ? (
                      <div className="mt-1.5 flex gap-1.5">
                        <input
                          className="field-input !w-auto flex-1 !py-1"
                          placeholder="Notes (optional)"
                          value={referralNotes[r.id] ?? ''}
                          onChange={(e) => setReferralNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                        />
                        {(['CLEARED', 'OBJECTED', 'WAIVED'] as const).map((decision) => (
                          <button
                            key={decision}
                            className="btn-secondary !px-2 !py-1 text-[11px]"
                            disabled={busy}
                            onClick={() =>
                              act(() =>
                                api.post(`/api/v1/m35/fuel-facility-licences/${id}/referrals/${r.id}/respond`, {
                                  decision,
                                  notes: referralNotes[r.id] || undefined,
                                }),
                              )
                            }
                          >
                            {decision}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>

          {isOfficer && l.state === 'REFERRALS_PENDING' ? (
            <button className="btn-primary" disabled={busy || !allReferralsResolved} onClick={() => act(() => api.post(`/api/v1/m35/fuel-facility-licences/${id}/claim-review`))}>
              {allReferralsResolved ? 'Claim for review' : 'Waiting on referral responses…'}
            </button>
          ) : null}

          {isOfficer && l.state === 'UNDER_REVIEW' ? (
            <div className="flex flex-wrap gap-2">
              <button className="btn-success" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m35/fuel-facility-licences/${id}/approve`))}>
                Approve (creates SeyPay fee intent)
              </button>
              <div className="flex gap-2">
                <input className="field-input" placeholder="Rejection reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                <button className="btn-danger" disabled={busy || rejectReason.trim().length < 3} onClick={() => act(() => api.post(`/api/v1/m35/fuel-facility-licences/${id}/reject`, { reason: rejectReason }))}>
                  Reject
                </button>
              </div>
            </div>
          ) : null}

          {l.state === 'APPROVED' && l.feeStatus !== 'PAID' ? (
            <p className="text-xs text-amber-600">Fee intent {l.feeIntentRefNo} pending — licence issuance arrives via the SeyPay webhook (service-token only) once paid.</p>
          ) : null}

          {isOfficer && l.state === 'ISSUED' ? (
            <div className="flex gap-2">
              <input className="field-input" placeholder="Suspend reason" value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} />
              <button className="btn-secondary" disabled={busy || suspendReason.trim().length < 3} onClick={() => act(() => api.post(`/api/v1/m35/fuel-facility-licences/${id}/suspend`, { reason: suspendReason }))}>
                Suspend
              </button>
              <input className="field-input" placeholder="Revoke reason" value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} />
              <button className="btn-danger" disabled={busy || revokeReason.trim().length < 3} onClick={() => act(() => api.post(`/api/v1/m35/fuel-facility-licences/${id}/revoke`, { reason: revokeReason }))}>
                Revoke
              </button>
            </div>
          ) : null}

          {isOfficer && l.state === 'SUSPENDED' ? (
            <button className="btn-success" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m35/fuel-facility-licences/${id}/reinstate`))}>
              Reinstate
            </button>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function CreateModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const operatorsQ = useApiQuery<Page<UtilityOperator>>('/api/v1/m35/operators?limit=100');
  const [operatorId, setOperatorId] = useState('');
  const [facilityName, setFacilityName] = useState('');
  const [facilityType, setFacilityType] = useState<FuelFacilityType>('FUEL_STATION');
  const [parcelNo, setParcelNo] = useState('');
  const [district, setDistrict] = useState('');
  const [storageCapacityLitres, setStorageCapacityLitres] = useState('');
  const [fuelTypes, setFuelTypes] = useState<string[]>(['PETROL']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleFuel = (t: string) => setFuelTypes((f) => (f.includes(t) ? f.filter((x) => x !== t) : [...f, t]));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m35/fuel-facility-licences', {
        operatorId,
        facilityName,
        facilityType,
        parcelNo,
        district,
        storageCapacityLitres: Number.parseFloat(storageCapacityLitres),
        fuelTypes,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to submit application');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New fuel facility licence application" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Operator">
          <select className="field-input" value={operatorId} onChange={(e) => setOperatorId(e.target.value)}>
            <option value="">Select an operator…</option>
            {operatorsQ.data?.items.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Facility name">
            <input className="field-input" value={facilityName} onChange={(e) => setFacilityName(e.target.value)} />
          </Field>
          <Field label="Facility type">
            <select className="field-input" value={facilityType} onChange={(e) => setFacilityType(e.target.value as FuelFacilityType)}>
              {FACILITY_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Parcel number">
            <input className="field-input" value={parcelNo} onChange={(e) => setParcelNo(e.target.value)} />
          </Field>
          <Field label="District">
            <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} />
          </Field>
          <Field label="Storage capacity (litres)">
            <input className="field-input" type="number" value={storageCapacityLitres} onChange={(e) => setStorageCapacityLitres(e.target.value)} />
          </Field>
        </div>
        <Field label="Fuel types">
          <div className="flex flex-wrap gap-1.5">
            {FUEL_TYPES.map((t) => (
              <label
                key={t}
                className={`flex cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition ${
                  fuelTypes.includes(t) ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-300 text-slate-600'
                }`}
              >
                <input type="checkbox" className="hidden" checked={fuelTypes.includes(t)} onChange={() => toggleFuel(t)} />
                {t}
              </label>
            ))}
          </div>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={busy || !operatorId || !facilityName || !parcelNo || !district || !storageCapacityLitres || fuelTypes.length === 0}
            onClick={submit}
          >
            {busy ? 'Submitting…' : 'Submit application'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
